
/**
 * `SheetHandle`: a caller's lease on an open sheet, and the published
 * namespace of the types a sheet is opened and edited through. The sheet
 * itself — rows, cells, model, metadata — is the shared `SheetHandleCore`
 * (`sheet-handle-core.ts`) every lease on it answers from; this module only
 * reads it (a type-only import), so it never loads the core. `SheetHandle` is
 * published from `client.ts` (and the barrels above it).
 */
import type { InstallPacer } from "@tento-lona/sheets/platform";
import { UserId } from "@tento-lona/sheets";
import type { Option, Unsubscribe } from "@tento-lona/sheets";
import { OrderedTree } from "@tento-core/ordered-tree";
import { Rational } from "@tento-core/rational";
import { RowKey, SheetNetworkKey } from "@tento-lona/sheets";
import { Patch, type RowTypeName } from "@tento-lona/sheets";
import { SheetKey } from "./sheet-key.ts";
import { Network } from "../network.ts";
import type { CellTarget } from "../cells/cell-target.ts";
import { RowLocalId } from "@tento-lona/sheets";
import {
  leasedRowOf,
  SheetRow,
} from "../rows/sheet-row.ts";
import { type SheetRowReads } from "../rows/sheet-row-reads.ts";
import type { RenderCursors, SceneNodeStamp } from "@tento-lona/sheets/platform";
import type { CellHandle } from "../cells/cell-handle.ts";
import { DateTime, NaiveDate, Utc } from "@tento-chrono";
import { RowAccessor } from "../rows/rows-accessor.ts";
import { SheetHost } from "./sheet-host.ts";
import { Actor } from "../invalidation.ts";
import {
  InvalidationSink,
} from "../invalidation.ts";
import type { Timescale } from "../view-model/view-model.ts";
import {
  SystemRow,
  type SystemRowsConfig,
  RowNetworkKey,
} from "@tento-lona/sheets";
import type { IngestionGate } from "@tento-lona/sheets/platform";
import type { SheetModel } from "@tento-lona/sheets";
import { type CellValue, Json, type JsonObject } from "@tento-lona/sheets";
import type {
  CalendarSystem,
} from "../preferences/calendar-preferences.ts";
import {
  type DependencyGraphReads,
} from "../formula/dependency-graph.ts";
import { RowId } from "@tento-lona/sheets";
import { type SyncBackend } from "../sync/sync-backend.ts";
import type {
  Backend,
  AddRowSpec,
  RawSheet,
  SheetModelReads,
} from "@tento-lona/sheets";
import { seedCells } from "@tento-lona/sheets/platform";
import { RowCellCache } from "../sync/row-cell-cache.ts";
import { type Graph } from "@tento-lona/sheets";
import { SheetRowTree } from "../rows/sheet-tree.ts";
import { SheetId } from "@tento-lona/sheets";
import { DisposeFailures, SheetHandleDisposedError } from "./handle-errors.ts";
import { deepFrozen } from "@tento-lona/sheets/internal-sdk";
import type { EphemeralSheets } from "./ephemeral-spec.ts";
import type { SheetHandleCore } from "./sheet-handle-core.ts";

/**
 * `SheetHandle`'s one friend door (its constructor is private): a caller's
 * lease on a core (`holds`: it takes one hold, released by its `dispose`; a
 * view that holds nothing otherwise — `peekOpenHandle`, a lease the SDK
 * hands its own modules), and the shared core behind a lease. Assigned by
 * `SheetHandle`'s static block; read through `leaseOver` / `coreOfLease`
 * (the client and the core module).
 */
let leaseDoor: {
  readonly lease: (core: SheetHandleCore, holds: boolean) => SheetHandle;
  readonly coreOf: (lease: SheetHandle) => SheetHandleCore;
};

/** A caller's lease on `core` (`SheetHandleCore.Port.lease`): `holds` takes one hold, released by its `dispose`. */
export function leaseOver(core: SheetHandleCore, holds: boolean): SheetHandle {
  return leaseDoor.lease(core, holds);
}

/** The shared core behind `lease` (`SheetHandleCore.Port.coreOf`). */
export function coreOfLease(lease: SheetHandle): SheetHandleCore {
  return leaseDoor.coreOf(lease);
}

/**
 * One caller's open sheet (`client.open`, `openBuiltin`, `openEphemeral`,
 * `parseSheet`): its rows, read-only cells, model, metadata and reactive
 * view model, disposed with `using`.
 *
 * The client keeps one open sheet per sheet and hands every open of it its
 * own `SheetHandle` — a lease on that shared sheet. Each lease is released
 * once by its own `dispose` (idempotent: a second `dispose` of the same
 * handle releases nothing); after it, every verb through THAT handle —
 * `cells` / `rows` / `model` and their fetches, `joinedTree`, `tree`,
 * `serialize`, `onChange`, the local edits, `ensurePromoted` — throws
 * `SheetHandle.DisposedError`, while another caller's handle on the same
 * sheet stays live. The sheet itself is torn down on its last lease's
 * release (its subscriptions, cached cells and place in the client's
 * cache), so the next open parses it afresh. What a handle handed out
 * before its dispose — a row, a `cells` facade, a cell subscription —
 * belongs to the shared sheet, and is refused once the sheet is torn down.
 * A handle whose sheet was torn down under it — the client's `dispose`, a
 * deleted sheet's eviction, or (for `client.peekOpenHandle`'s view, which
 * holds nothing) the last holder's release — refuses every verb the same
 * way, disposed or not; only its identity reads (`id`, `key`, `title`, …)
 * still answer. Two opens of one sheet are two handles (`!==`); compare
 * them by `id`.
 */
export class SheetHandle {
  static readonly ACTOR = new Actor("sheet-handle");
  /**
   * The actor a held row (`SheetRow.Handle`, from `handle.rows` or
   * `client.rows`) stamps its own corrections with: a refused write's
   * take-back, a checked update's reread. A surface that skips its own echo
   * still repaints for it. (`SheetRow` is a type-only export, so the row
   * class's own `ACTOR` is published here.)
   */
  static readonly ROW_ACTOR: Actor = SheetRow.Handle.ACTOR;
  static {
    leaseDoor = {
      lease: (core, holds) => new SheetHandle(core, holds),
      coreOf: (lease) => lease.#core,
    };
  }

  /** The shared open sheet. */
  readonly #core: SheetHandleCore;
  /** Whether this lease holds the core (`false`: a view that holds nothing). */
  readonly #holds: boolean;
  /** Set by this lease's `dispose`. */
  #released = false;
  /** This lease's `cells` facade (its verbs check this lease first). */
  #cellsView: Option<SheetHandle.Cells> = null;
  /** This lease's `rows` accessor (its verbs, and the rows it hands out,
   *  check this lease first). */
  #rowsView: Option<RowAccessor> = null;
  /** This lease's `model` face, per rows-side face it forwards to. */
  #modelView: Option<{ readonly shared: SheetModel; readonly view: SheetModel }> = null;
  /** This lease's view of each shared held row it handed out (`heldRow`):
   *  one per row, so a lease's row keeps its identity. */
  readonly #rowViews = new WeakMap<SheetRow.Handle, SheetRow.Handle>();
  /** The change subscriptions taken through this lease (`onChange`,
   *  `cells.onChange`), released by its `dispose`. */
  readonly #subscriptions = new Set<() => void>();
  /** The progressive reads the rows this lease handed out started
   *  (`row.cell` / `row.cells`, `#leaseOwner`), failed and released by its
   *  `dispose`. */
  readonly #releaseWatchers = new Set<() => void>();

  private constructor(core: SheetHandleCore, holds: boolean) {
    if (holds) core.hold("open");
    this.#core = core;
    this.#holds = holds;
  }

  /**
   * Throw `SheetHandle.DisposedError` once this lease was disposed — or its
   * sheet was torn down under it (the client's `dispose`, a deleted sheet's
   * eviction, the last holder's release under a view that holds nothing):
   * a lease never answers from a dead sheet.
   */
  #assertHeld(verb: string): void {
    if (this.#released || this.#core.disposed) throw new SheetHandle.DisposedError(verb);
  }

  /**
   * Keep `unsubscribe` (a change subscription taken through this lease)
   * until this lease's `dispose` releases it; the returned unsubscribe
   * releases it once, earlier.
   */
  #leaseSubscription(unsubscribe: () => void): () => void {
    let live = true;
    const release = (): void => {
      if (!live) return;
      live = false;
      this.#subscriptions.delete(release);
      unsubscribe();
    };
    this.#subscriptions.add(release);
    return release;
  }

  /**
   * The rows-side model (see `SheetModel`), through this handle: its
   * reads answer from the shared sheet, and its subscription
   * (`subscribeCanonicalCells`) refuses with `SheetHandle.DisposedError`
   * after this handle's `dispose`, also through a model taken before. The
   * subscriptions taken through it are released by the handle's `dispose`.
   * It writes nothing: a surface row is `handle.rows.ensureSurface(kind,
   * actor)`.
   */
  get model(): SheetModel {
    this.#assertHeld("model");
    const shared = this.#core.model;
    if (this.#modelView?.shared === shared) return this.#modelView.view;
    const held = (verb: string): void => this.#assertHeld(verb);
    const lease = (unsubscribe: () => void): (() => void) => this.#leaseSubscription(unsubscribe);
    const subscribeCanonicalCells: SheetModel["subscribeCanonicalCells"] = (key, onChange) => {
      held("model.subscribeCanonicalCells");
      return lease(shared.subscribeCanonicalCells(key, onChange));
    };
    // The face is frozen: the view is a proxy over a copy of it (the copy's
    // own members stay configurable), every read forwarded live to `shared`.
    const view = new Proxy({ ...shared }, {
      get: (_copy, key) => {
        switch (key) {
          case "subscribeCanonicalCells":
            return subscribeCanonicalCells;
          default:
            return Reflect.get(shared, key, shared);
        }
      },
      set: () => false,
    });
    this.#modelView = { shared, view };
    return view;
  }

  /**
   * This lease as the owner of a progressive read (`row.cell`, `row.cells`)
   * built by `verb` on a row it handed out: live while this lease is held,
   * and its `dispose` fails the read (`SheetHandle.DisposedError`) and
   * releases it — whatever other lease still holds the sheet. The row
   * combines it with the sheet's own lifetime (`leasedRowOf`).
   */
  #leaseOwner(verb: string): CellHandle.Owner {
    return {
      assertLive: () => this.#assertHeld(verb),
      watch: (onDisposed) => {
        const watcher = (): void => onDisposed(new SheetHandle.DisposedError(verb));
        this.#releaseWatchers.add(watcher);
        return () => {
          this.#releaseWatchers.delete(watcher);
        };
      },
    };
  }

  /** This lease's view of the shared held row `row` (`leasedRowOf`). */
  #heldRow(row: SheetRow.Handle): SheetRow.Handle {
    const known = this.#rowViews.get(row);
    if (known != null) return known;
    const view = leasedRowOf(row, (verb) => this.#assertHeld(verb), (verb) => this.#leaseOwner(verb));
    this.#rowViews.set(row, view);
    return view;
  }

  /** Bumped by every structural change of the sheet. */
  get generation(): number {
    return this.#core.generation;
  }

  /** The sheet's formula dependency graph, read-only. */
  get graph(): DependencyGraphReads<RowLocalId> {
    return this.#core.graph;
  }

  /** The chrome (system) rows' ids, read-only. */
  get systemRowIds(): ReadonlySet<RowLocalId> {
    return this.#core.systemRowIds;
  }

  /** The footer surface's row ids — a subset of `systemRowIds` — read-only. */
  get footerRowIds(): ReadonlySet<RowLocalId> {
    return this.#core.footerRowIds;
  }

  /**
   * This sheet's cells: loads, cached reads and change subscriptions (see
   * `SheetHandle.Cells`), through this handle: after its `dispose` the
   * facade's load (`ensureLoaded`) and its subscription (`onChange`) throw
   * `SheetHandle.DisposedError`, also through a facade taken before; its
   * cached read (`peekInRange`) stays allowed. The `onChange`
   * subscriptions taken through it are released by the handle's `dispose`.
   */
  get cells(): SheetHandle.Cells {
    if (this.#cellsView != null) return this.#cellsView;
    const shared = this.#core.cells;
    const held = (verb: string): void => this.#assertHeld(verb);
    const view: SheetHandle.Cells = Object.freeze<SheetHandle.Cells>({
      peekInRange: shared.peekInRange,
      ensureLoaded: (range) => {
        held("cells.ensureLoaded");
        return shared.ensureLoaded(range);
      },
      onChange: (cb) => {
        held("cells.onChange");
        return this.#leaseSubscription(shared.onChange(cb));
      },
    });
    this.#cellsView = view;
    return view;
  }

  get network(): Network.UserSheet {
    return this.#core.network;
  }

  get isTemplate(): boolean {
    return this.#core.isTemplate;
  }

  get systemRowFactory(): Option<SystemRow.Factory> {
    return this.#core.systemRowFactory;
  }

  get persistence(): "local" | "synced" {
    return this.#core.persistence;
  }

  get title(): Option<string> {
    return this.#core.title;
  }

  get description(): Option<string> {
    return this.#core.description;
  }

  /**
   * The sheet's id: its interned `SheetId`, so two handles of one sheet
   * compare `===` (`client.sheets.get(handle.id)`). The wire spelling the
   * list and create routes answer is `network.id`; the text a route takes is
   * `SheetNetworkKey.of(id)`.
   */
  get id(): SheetId {
    return this.#core.id;
  }

  get preferredColumnWidthPx(): Option<number> {
    return this.#core.preferredColumnWidthPx;
  }

  get editable(): boolean {
    return this.#core.editable;
  }

  get key(): SheetKey {
    return this.#core.key;
  }

  get invalidation(): InvalidationSink {
    return this.#core.invalidation;
  }

  sheetRow(id: RowLocalId): Option<SheetRowReads> {
    this.#assertHeld("sheetRow");
    return this.#core.sheetRow(id);
  }

  /**
   * The sheet's rows (`RowAccessor`), through this handle — refused once it
   * was disposed. Its persisting verbs (`add`, `delete`, `move`,
   * `hydrateChildren`) check this handle first, so an accessor taken before
   * its `dispose` rejects with `SheetHandle.DisposedError` afterwards and
   * sends nothing; its cached reads (`peek`, `peekAll`) stay allowed. The
   * rows it hands out are this handle's too: the shared row's state, whose
   * network verbs (`update`, `write`, `cells`, …) refuse the same way after
   * this handle's `dispose`, while another handle's rows keep working.
   */
  get rows(): RowAccessor {
    this.#assertHeld("rows");
    if (this.#rowsView != null) return this.#rowsView;
    const shared = this.#core.rows;
    const held = (verb: string): void => this.#assertHeld(verb);
    const rowOf = (row: SheetRow.Handle): SheetRow.Handle => this.#heldRow(row);
    const peek: RowAccessor["peek"] = (query) => {
      const row = shared.peek(query);
      return row == null ? null : rowOf(row);
    };
    const peekAll: RowAccessor["peekAll"] = () => shared.peekAll().map(rowOf);
    const add: RowAccessor["add"] = async (spec, actor) => {
      held("rows.add");
      const added = await shared.add(spec, actor);
      return Object.freeze({ row: rowOf(added.row), styled: added.styled });
    };
    const ensureSurface: RowAccessor["ensureSurface"] = async (kind, actor) => {
      held("rows.ensureSurface");
      return rowOf(await shared.ensureSurface(kind, actor));
    };
    const del: RowAccessor["delete"] = async (row, actor) => {
      held("rows.delete");
      const deletion = await shared.delete(row, actor);
      if (deletion.outcome !== "deleted") return deletion;
      // The undo's restored row is this handle's too.
      const undo = deletion.undo;
      return {
        outcome: "deleted",
        undo: async (undoActor: Actor) => {
          held("rows.restore");
          return rowOf(await undo(undoActor));
        },
      };
    };
    const move: RowAccessor["move"] = async (row, opts, actor) => {
      held("rows.move");
      return await shared.move(row, opts, actor);
    };
    const moveAfter: RowAccessor["moveAfter"] = async (row, opts, actor) => {
      held("rows.moveAfter");
      return await shared.moveAfter(row, opts, actor);
    };
    const hydrateChildren: RowAccessor["hydrateChildren"] = async (row, children, actor) => {
      held("rows.hydrateChildren");
      const hydrated = await shared.hydrateChildren(row, children, actor);
      return hydrated.map((entry) => ({ ...entry, row: rowOf(entry.row) }));
    };
    // `RowAccessor` is a class with private state: the view is a proxy over
    // the shared accessor, its verbs replaced by this lease's.
    const view = new Proxy(shared, {
      get: (target, key) => {
        switch (key) {
          case "peek":
            return peek;
          case "peekAll":
            return peekAll;
          case "add":
            return add;
          case "ensureSurface":
            return ensureSurface;
          case "delete":
            return del;
          case "move":
            return move;
          case "moveAfter":
            return moveAfter;
          case "hydrateChildren":
            return hydrateChildren;
          default:
            return Reflect.get(target, key, target);
        }
      },
      set: () => false,
    });
    this.#rowsView = view;
    return view;
  }

  detachAlias(id: RowLocalId): Promise<ReadonlyMap<RowLocalId, RowLocalId>> {
    try {
      this.#assertHeld("detachAlias");
    } catch (err) {
      return Promise.reject(err);
    }
    return this.#core.detachAlias(id);
  }

  ensurePromoted(): Promise<void> {
    try {
      this.#assertHeld("ensurePromoted");
    } catch (err) {
      return Promise.reject(err);
    }
    return this.#core.ensurePromoted();
  }

  /** Subscribe to the sheet's cell changes through this handle (as
   *  `cells.onChange`); released by the handle's `dispose`. */
  onChange(cb: SheetHandle.Cells.ChangeListener): Unsubscribe {
    this.#assertHeld("onChange");
    return this.#leaseSubscription(this.#core.onChange(cb));
  }

  /**
   * The graph props a data row's virtual children describe; `null` when
   * the row has none or is not a data row. `row` is the held row or its
   * branded id (`SheetHandle.RowRef`), never a string.
   */
  graphPropsFor(row: SheetHandle.RowRef): Option<Graph.Props> {
    this.#assertHeld("graphPropsFor");
    return this.#core.graphPropsFor(row);
  }

  joinedTree(config: {
    timescale: Timescale;
    host: SheetHost;
    invalidation: InvalidationSink;
  }): SheetRowTree {
    this.#assertHeld("joinedTree");
    return this.#core.joinedTree(config);
  }

  /** The sheet's row tree (`SheetHandle.Tree`): each row's node, parent, children and metadata. */
  tree(): SheetHandle.Tree {
    this.#assertHeld("tree");
    return this.#core.tree();
  }

  serialize(): Network.UserSheetWithRows {
    this.#assertHeld("serialize");
    return this.#core.serialize();
  }

  /**
   * Release this handle: once per handle — a second `dispose` of it changes
   * nothing — and only this caller's: another caller's handle on the same
   * sheet stays live. The change subscriptions taken through this handle
   * (`onChange`, `cells.onChange`) are released with it; another handle's
   * keep firing. Afterwards every verb through this handle throws
   * `SheetHandle.DisposedError`. The sheet's last handle released tears the
   * sheet down (see `SheetHandle`). A view that holds nothing
   * (`client.peekOpenHandle`) releases nothing. Also `[Symbol.dispose]`, so
   * a script writes `using handle = await client.open(key);`.
   */
  dispose(): void {
    if (this.#released) return;
    this.#released = true;
    const failures = new DisposeFailures();
    for (const release of [...this.#subscriptions]) failures.run(release);
    // The reads this lease's rows started end with it, not with the sheet.
    const reads = [...this.#releaseWatchers];
    this.#releaseWatchers.clear();
    for (const fail of reads) failures.run(fail);
    if (this.#holds) failures.run(() => this.#core.release());
    failures.rethrow("SheetHandle.dispose: a disposal callback threw");
  }

  [Symbol.dispose](): void {
    this.dispose();
  }
}

/**
 * Types and values that go with `SheetHandle`: `InUseError`, `DisposedError`,
 * `RowRef`, `Cells`, `Local`, `Platform`, and 14 more.
 */
export namespace SheetHandle {
  /**
   * `openEphemeral(spec, { onExisting: "exclusive" })` while a handle on
   * `spec.id` is open: opening `spec` over it would tear down a sheet
   * another caller holds (and its handles with it). Dispose the open
   * handles first, or open with `onExisting: "reuse"`. `sheetId` is the
   * sheet; `holders` the handles that hold it.
   */
  export class InUseError extends Error {
    readonly sheetId: SheetId;
    readonly holders: number;
    constructor(sheetId: SheetId, holders: number) {
      super(
        `SheetHandle: openEphemeral(exclusive) of ${sheetId} while ${holders} handle(s) hold it; ` +
          "dispose them first, or open with onExisting: \"reuse\"",
      );
      this.name = "SheetHandle.InUseError";
      this.sheetId = sheetId;
      this.holders = holders;
    }
  }

  /**
   * A fetch or write through a `SheetHandle` after `handle.dispose()` (or the
   * end of its `using` block). The handle is out of the client's cache;
   * reopen the sheet with `client.open(...)`. `verb` names what was attempted.
   */
  export const DisposedError: typeof SheetHandleDisposedError = SheetHandleDisposedError;
  /** See the value `SheetHandle.DisposedError`. */
  export type DisposedError = SheetHandleDisposedError;

  /**
   * The row a sheet-wide read (`handle.cells`) addresses: the held row
   * itself (a `SheetRow.Handle` or `SheetRow.Joined.Handle` — anything with
   * its `id: RowLocalId`), its interned `RowLocalId`, or a parsed `RowId`.
   * Never a bare string: a row id is branded, so an unrelated string (a
   * label, a sheet id) cannot be passed by accident. Any id of a
   * lookup-keyed row reaches the same cells.
   */
  export type RowRef = RowLocalId | RowId | { readonly id: RowLocalId };

  export namespace RowRef {
    /**
     * The wire key a `RowRef` addresses. A bare string from an untyped
     * caller (the retired form) throws a `TypeError` naming the fix.
     */
    export function key(ref: RowRef): RowNetworkKey {
      if (ref instanceof RowLocalId || ref instanceof RowId) return RowNetworkKey.of(ref);
      const held: unknown = ref;
      if (typeof held !== "object" || held === null) {
        throw new TypeError(
          "handle.cells: address a row by the held row or its RowLocalId / RowId " +
            "(RowId.of(key) for a wire key), not a string",
        );
      }
      return RowNetworkKey.of(ref.id);
    }
  }

  /**
   * A sheet's cells, as a consumer reads them sheet-wide (`handle.cells`):
   * fetch a range for every row at once (`fetch`), then read the cache per
   * row (`peekInRange`), and hear what changed (`onChange`). For ONE held
   * row the read is `row.cells(range)` (progressive, both lanes, each entry
   * carrying its address); a single-cardinality row's one cell of a column
   * is `row.single()?.get(day)` / `peek(day)` / `cell(day)`. Every entry is
   * a `SheetRow.Cell` — its `kind` and the row it was read for
   * (`entry.row`) — so it feeds `CellTarget.cell`, and another row's verbs
   * refuse it. Read-only: a cell is written through the writable facade its
   * row hands out (`row.writableSingle()` / `row.writableMulti()`): its
   * `write(target, value, actor)` (checked) or `writeDurable(target, value,
   * actor)` — the one cell write path. A
   * row is addressed by a `RowRef` (the held row or its branded id). The
   * cache's scheduling seams (`isStale`, `invalidateRow`,
   * `forgetFetchCoverage`, `startFetch`, …) are the renderer host's, on
   * `platformOf(handle)`.
   */
  export interface Cells {
    /**
     * The cached cells of a row over `range`: over a `NaiveDate.Range`, its
     * date-keyed cells (`SheetRow.DateCell`); over a `DateTime.Range<Utc>`,
     * its timepoint (sub-day) cells (`SheetRow.TimepointCell`). Never
     * loads: `ensureLoaded(range)` first.
     */
    peekInRange(row: RowRef, range: NaiveDate.Range): readonly SheetRow.DateCell[];
    peekInRange(row: RowRef, range: DateTime.Range<Utc>): readonly SheetRow.TimepointCell[];
    /**
     * Load every row's cells for `range` in one request, unless an earlier
     * load already covers it (then it resolves at once, no request) — the
     * one load path (`handle.cells.ensureLoaded(range)`, then `peekInRange`
     * reads the cache). A renderer's scheduling seams (`startFetch`,
     * `ensureFresh`) are on `platformOf(handle)`.
     */
    ensureLoaded(range: NaiveDate.Range): Promise<void>;
    /** Subscribe to cell changes (stale row ids + range); returns the unsubscribe. */
    onChange(cb: Cells.ChangeListener): Unsubscribe;
  }

  export namespace Cells {
    /**
     * What a `cells.onChange` subscriber hears: the rows whose cells went
     * stale (by local id) and the dates the change covers (end exclusive;
     * `null`: the whole row — a write or an explicit invalidation).
     */
    export type ChangeListener = (staleRows: ReadonlySet<RowLocalId>, range: Option<NaiveDate.Range>) => void;
  }

  /**
   * The local-only edits (`platformOf(handle).local`): tree mutations, cell
   * paints and the row-list seams. They change this handle's copy of the
   * sheet — joined-side tree and rows-side sheet together — and emit the
   * matching invalidation. None persists; a tree edit's row must already be
   * registered with the client (`platformOf(client).registerRow`).
   */
  export interface Local {
    /**
     * Put a row into this handle's copy of the sheet without persisting it —
     * a template preview's row toggle. Seeds `spec.row` into the rows-side
     * sheet (unless a row with its id is already there), paints `spec.cells`
     * into its cached cells, then inserts it at the top level at
     * `spec.position` and emits `NewRowInserted`, in that order, all stamped
     * `actor`. The row must be registered with the client (a template's
     * rows are, through the backend the preview was parsed over). Returns
     * the row's `RowLocalId`. A persisted row is `handle.rows.add(spec,
     * actor)`.
     */
    add(spec: LocalRowSpec, actor: Actor): RowLocalId;
    /** Insert a registered row at the top level; emits `NewRowInserted` stamped `actor`. */
    addRoot(id: RowLocalId, position: Rational, actor: Actor): void;
    /**
     * Take a row (and its subtree) out of this handle's copy of the sheet —
     * the inverse of `add` — and emit `LayoutChanged` stamped
     * `actor`. Nothing is deleted on the server: a persisted delete is
     * `handle.rows.delete(ref, actor)`.
     */
    delete(id: RowLocalId, actor: Actor): void;
    /**
     * Patch a registered row's raw data. Throws when `id` is not registered —
     * a patch that lands nowhere is a caller bug, not a no-op. Emits
     * `RowDataChanged` for the row (and, when its formula changed, for each
     * row that depends on it) stamped `actor`.
     */
    update(id: RowLocalId, patch: LocalRowPatch, actor: Actor): void;
    /**
     * Move a row under `parent` (`null`: top level) and/or to `position`,
     * and emit `RowsReordered` stamped `actor`.
     */
    move(
      id: RowLocalId,
      parent: Option<RowLocalId>,
      position: Option<Rational>,
      actor: Actor,
    ): void;
    /**
     * Re-derive what the tree's structure feeds — the label / lookup-key maps
     * and the formula dependency graph — after an edit that bypassed `local`.
     * Emits `SheetStructureChanged`.
     */
    invalidateStructure(): void;
    /**
     * Paint a cell edit that already happened elsewhere (an agent's
     * ephemeral write, a preview's sample value) into the cached cell of
     * `row` that `target` addresses, and repaint its readers. No promote, no
     * request, no verdict — a user's cell write is a writable facade's
     * `write` / `writeDurable`, the one cell write path of a `SheetHandle`.
     * `target` is a single-cardinality row's date-keyed cell
     * (`CellTarget.date`) or a multi-cardinality row's held / id-keyed cell
     * (`CellTarget.cell`, `CellTarget.keyed`, `CellTarget.addWithId`); a
     * `date` on a multi-cardinality row names no cell, and an id names none
     * of a single-cardinality row's (its one cell per column has no id):
     * either throws `CellWriteError` (outcome `unaddressable`), nothing
     * painted. `actor` stamps the write (`Actor.EXTERNAL` for an edit no
     * surface owns), so the originating surface can skip its own echo.
     */
    writeCell(
      row: RowRef,
      target: CellTarget.DateKeyed,
      data: Json | CellValue,
      actor: Actor,
    ): void;
    /**
     * Clear the cell of `row` that `target` addresses — the inverse of
     * `writeCell`, addressed and checked the same way. A user's clear is
     * a writable facade's `clear` / `clearDurable`. The clear goes through the client's
     * local-edit chokepoint (`platformOf(client).local.clearCell`), so every
     * surface showing the row drops the cell; `actor` stamps it.
     */
    clearCell(row: RowRef, target: CellTarget.DateKeyed, actor: Actor): void;
    /**
     * Write `cells` into `row`'s rows-side cell store and emit the ordinary
     * cell-change events stamped `actor`, so formulas and renderers follow —
     * for a front-end rendering an offline or ephemeral sheet (the CLI's
     * seeded render). A user edit is the row's `write`.
     */
    seedCells(
      row: RowRef,
      cells: Iterable<readonly [NaiveDate.Partial, Json | CellValue]>,
      actor: Actor,
    ): void;
    /**
     * Adopt a row the server created elsewhere (a host's own POST): register
     * it with the client, list it, mirror it into the rows-side sheet and
     * insert it at the top level, announced as `actor`. Nothing is sent.
     */
    adoptCreated(created: Network.CreateRowResponse, actor: Actor): SheetRow.Handle;
    /** Register an externally created row in the row list only (no tree, no announcement). */
    pushToList(row: SheetRow.Handle): void;
  }

  /**
   * The renderer host's seam onto a sheet (`platformOf(handle)`,
   * `@tento/lona/host`), for a render front-end — the sheets-ui
   * `SheetElement` that drives scrolling, sheet switches and the render-skip
   * ledger, the CLI's tree / TUI renderers — and the one home of every
   * local-only paint (no request, no verdict), which application code
   * reaches only to mirror a change it persisted elsewhere or never persists
   * (a template preview's row toggle: `local.add` / `local.delete`).
   *
   * A local-only edit says so once, by its face: every one is on `local`
   * (`SheetHandle.Local`: the tree edits, `writeCell` / `clearCell` /
   * `seedCells`, `adoptCreated` / `pushToList`), and `platformOf(client)`'s
   * cell paints are on its `local` too, spelled and addressed alike.
   */
  export interface Platform {
    /**
     * The rows-side sheet the handle renders from, as its reads
     * (`SheetModelReads`: rows, tree, metadata, cells, the formula graph as
     * a view) — what a front-end walks to build its scene and tree. No
     * writer of any kind: `handle.model` carries the application's persisting verbs,
     * the local-only writes are `local` (`add`, `delete`, `writeCell`,
     * `clearCell`, `seedCells`, …), the alias fork is the handle's
     * own `detachAlias`, and the full
     * `Sheet` is a white-box door (`instrumentationOf(handle).sheet`,
     * `@tento-lona/internal-testing`).
     */
    readonly sheet: SheetModelReads;
    /**
     * The local-only edits (`SheetHandle.Local`): insert, remove, patch,
     * reparent a registered row in this handle's copy of the sheet, paint,
     * clear or seed its cells, adopt a row the server created — announced,
     * never sent. For a host mirroring a change it persisted elsewhere (a
     * template preview, a boot seed).
     */
    readonly local: Local;
    /** `handle.cells.ensureLoaded` without awaiting it (a renderer's scroll); a failure is logged, not thrown. */
    startFetch(range: NaiveDate.Range): void;
    /** Make `row`'s cells fresh for `range`: re-evaluate a formula, else refetch. */
    ensureFresh(row: RowRef, range: NaiveDate.Range): Promise<void>;
    /** Every cached date-keyed cell of a row (a formula's read of it). */
    peekAll(row: RowRef): readonly SheetRow.DateCell[];
    /** Whether a row's cached cells are stale (a dependency changed). */
    isStale(row: RowRef): boolean;
    /** Mark a row's cached cells stale so its next read re-derives them. */
    invalidateRow(row: RowRef): void;
    /**
     * Forget the sheet-wide fetch coverage, so the next `handle.cells.ensureLoaded`
     * of an already-fetched range is a real request (after a row that
     * carries server-derived data joins the sheet).
     */
    forgetFetchCoverage(): void;
    /**
     * Every cell `row` (the held row or its branded id) holds in `range`,
     * date-keyed and timepoint cells tagged by `kind`: the row's cell handle's
     * `cellsIn`, minting that handle on first use as a cell read does.
     */
    cellsIn(row: RowRef, range: NaiveDate.Range): RowCellCache.Cell[];
    /**
     * Park cell and rows-side responses behind `gate` while it holds (a live
     * gesture); replacing a gate wakes its waiters.
     */
    installIngestionGate(gate: IngestionGate): void;
    /** Owner-scoped removal: clears only while `owner` is the installed gate. */
    clearIngestionGate(owner: IngestionGate): void;
    /** Share one install-admission budget between cell and rows-side responses. */
    setInstallPacer(pacer: InstallPacer): void;
    /** Owner-scoped removal releases queued installs. */
    clearInstallPacer(owner: InstallPacer): void;
    /**
     * Mark the sheet shown (`true`) or switched away from (`false`); an
     * inactive sheet's in-flight fetch continuations bail. Idempotent.
     */
    setActive(active: boolean): void;
    /** Batch-prefetch the rows-side aggregates' data for `range`. */
    ensureRowsLoaded(range: NaiveDate.Range): Promise<void>;
    /**
     * The scoped form of `ensureRowsLoaded`, for a renderer behind the
     * `row-demand` flag: load `range` for the trees of `roots` (a resident
     * viewport's demand, `RowDemand.roots`) and no other row, each through
     * its own load (`ModelRow.ensureLoaded`), gathered into batches.
     */
    ensureRootsLoaded(range: NaiveDate.Range, roots: readonly RowLocalId[]): Promise<void>;
    /** The render-cursor registry of the rows-side ledger; `null` without one. */
    renderCursors(): Option<RenderCursors>;
    /**
     * The scene stamp a live scene diff keys `rowId` on (data address +
     * values/attr versions), or `null` when the row is unversioned.
     */
    sceneStamp(rowId: RowLocalId): Option<SceneNodeStamp>;
    /** The rows-side ledger's current tau; `0` without a ledger. */
    ledgerTau(): number;
    /** Re-evaluate every formula row of the rows-side sheet over `range` (after `local.seedCells`). */
    recomputeFormulas(range: NaiveDate.Range): void;
    /**
     * Render the rows-side sheet at `timeScale`: aliases whose template
     * branches on the time scale re-expand for it (the calendar alias swaps
     * `timeline[column]` ↔ `grid`). LOCAL ONLY — the sheet's persisted time
     * scale is `client.sheets.update`. A no-op at the current scale.
     */
    setTimescale(timescale: Timescale): void;
  }

  /**
   * A local-only row for `platformOf(handle).local.add`: the row to seed
   * (`AddRowSpec`, from `@tento/lona/sheets`), its top-level position, and the
   * cells to paint before it is announced (`[]`: none).
   */
  export interface LocalRowSpec {
    readonly row: AddRowSpec;
    readonly position: Rational;
    readonly cells: readonly (readonly [NaiveDate.Partial, Json | CellValue])[];
  }

  /**
   * What a handle is: a server-backed sheet (`"persisted"`), a local sheet
   * not yet saved (`"ephemeral"`: an edit promotes it), or an ephemeral sheet
   * opened from a template (`"template"`: the template's claim banner). One
   * declaration: the system rows' `SystemRowsConfig.Mode`.
   */
  export type Mode = SystemRowsConfig.Mode;

  /** How `openEphemeral` opens its spec: a `Mode` other than `"persisted"`. */
  export type EphemeralMode = "ephemeral" | "template";

  /**
   * A debug system row a handle can build: the scroll-info header row
   * (`"scroll-info"`) or the timeline-legend footer row (`"timeline-legend"`).
   * One declaration: the system rows' `SystemRowsConfig.DebugRow`.
   */
  export type DebugRow = SystemRowsConfig.DebugRow;

  /**
   * Options for `LonaSdk.Client.parseSheet`. Every field is required;
   * `null` is the stated default (the client's own seam). Start from
   * `SheetHandle.ParseConfig.DEFAULT`:
   * `client.parseSheet(data, { ...SheetHandle.ParseConfig.DEFAULT, backend: seeded })`.
   */
  export type ParseConfig = {
    /** What the parsed handle is (`SheetHandle.Mode`). */
    mode: Mode;
    /** The debug system rows to build (`[]`: none). */
    debugRows: readonly DebugRow[];
    /** The system-row factory; `null`: the default one. */
    factory: Option<SystemRow.Factory>;
    /**
     * Read cells through this sync backend instead of the client's — a
     * template preview's seeded `SeededSyncBackend`. One of the SDK's own
     * backends (`SeededSyncBackend`, `MemorySyncBackend`, `SyncHttpBackend`):
     * a handle reads cells through the backend's per-row caches, so
     * `parseSheet` refuses any other `SyncBackend` with a `TypeError`. Its cells stay in this handle:
     * it gets its own cell fan-out, never the client's shared one, so sample
     * data cannot reach a real sheet showing the same row. `null`: the
     * client's backend.
     */
    backend: Option<SyncBackend>;
    /**
     * Hydrate the rows-side sheet through a separate accessor over this
     * backend: the client's registry, alias templates and sync poll, with its
     * own sheet cache. `null`: the client's rows accessor.
     */
    rowsBackend: Option<Backend>;
    /**
     * Answer this preview's reads by row key (a row with a `lookupKey`)
     * through this source — a seeded preview's own keyed answers. Only for a
     * preview without a `rowsBackend` (that backend answers its keyed reads).
     * `null`: a preview with only a `backend` rejects keyed reads, naming the
     * preview, rather than reach the client's rows backend.
     */
    keyedReads: Option<Pick<Backend, "fetchCellsByKey">>;
    /**
     * Publish this handle's changes into this sink instead of the client's —
     * a preview rendered in its own sheet element, wired to its own sink.
     * Requires `rowsBackend` (the rows-side sheet publishes into the same
     * sink). `null`: the client's sink.
     */
    invalidation: Option<InvalidationSink>;
  };

  export namespace ParseConfig {
    /** Every option at its default: a plain server-backed sheet on the
     *  client's own backend, rows accessor and sink. */
    export const DEFAULT: ParseConfig = deepFrozen({
      mode: "persisted",
      debugRows: [],
      factory: null,
      backend: null,
      rowsBackend: null,
      keyedReads: null,
      invalidation: null,
    });
  }

  /**
   * Options for `SheetHandle.rebuildSystemRows`. Every field is required;
   * `null` is the stated default. Start from `RebuildConfig.DEFAULT`.
   */
  export type RebuildConfig = {
    /** The debug system rows to build (`[]`: none). */
    debugRows: readonly DebugRow[];
    /**
     * What a rebuild that changed the system rows announces:
     * `"structure-changed"` emits `SheetStructureChanged`; `"nothing"` for a
     * caller that emits its own.
     */
    emit: RebuildConfig.Emit;
    /** The time scale the system rows are built for; `null`: the sheet's. */
    timescale: Option<Timescale>;
    /** The secondary calendar systems to show; `null`: none. */
    calendarSystems: Option<readonly CalendarSystem[]>;
  };

  export namespace RebuildConfig {
    /** What a rebuild announces (`RebuildConfig.emit`). */
    export type Emit = "structure-changed" | "nothing";

    /** Every option at its default. */
    export const DEFAULT: RebuildConfig = deepFrozen({
      debugRows: [],
      emit: "structure-changed",
      timescale: null,
      calendarSystems: null,
    });
  }


  /**
   * Where a system row sits in a handle's tree, as `replaceSystemRows` takes
   * it: its id, position, whether it is a footer row (`null`: not), and its
   * child ids (`null`: none). The rows-side row itself is `@tento/lona/sheets`'
   * `SystemRowDef`.
   */
  export type SystemRowPlacement = {
    readonly id: RowLocalId;
    readonly position: Rational;
    readonly isFooter: Option<boolean>;
    readonly children: Option<RowLocalId[]>;
  };

  /**
   * Partial update for a registered row's LOCAL raw data (`local.update`),
   * in the `Patch` vocabulary: a field the patch names is set to its value or
   * cleared with `Patch.clear`, a field it omits is left as it is; nothing is
   * sent. The server row PATCH body is `RowPatch`.
   *
   * ```ts
   * platformOf(handle).local.update(id, { formula: Patch.clear }, actor);
   * ```
   */
  export type LocalRowPatch = {
    label?: Patch.Clearable<string>;
    heightPx?: Patch.Clearable<number>;
    lookupKey?: Patch.Clearable<string>;
    formula?: Patch.Clearable<string>;
    attributes?: Patch.Clearable<JsonObject>;
    metadata?: Patch.Clearable<JsonObject>;
    icon?: Patch.Clearable<string>;
  };

  export type RowQuery =
    // The row's id: its branded `RowLocalId` / `RowId` (`RowId.of(key)`
    // for a wire key).
    | { id: RowLocalId | RowId }
    | { lookupKey: string }
    // The row's type: a `RowTypeName` (`RowTypeName.of("calendar")`).
    | { type: RowTypeName }
    | { label: string };

  /**
   * A row `parseSheet` takes: a `Network.SheetRow`, or the fields of one a
   * local row states, with its seed cells (`cells`) when it has any.
   */
  export type ParseRow = SheetRow.Input & {
    id: { toString(): string };
    position: Rational | { n: number; d: number };
    children?: Option<Array<{ toString(): string }>>;
    cells?: Option<Network.SheetCell[]>;
  };

  /**
   * A sheet's row tree as `handle.tree()` reads it: each row's node (its id,
   * position and children, in order), a row's parent, and the rows' metadata.
   */
  export interface Tree {
    /** The top-level nodes, in order. */
    readonly nodes: readonly OrderedTree.Node<RowLocalId, Rational>[];
    /** The rows' metadata, by id. */
    readonly metadata: SheetRow.Registry;
    /** Row `id`'s node, or `null` when the tree has no such row. */
    get(id: RowLocalId): Option<OrderedTree.Node<RowLocalId, Rational>>;
    /** Row `id`'s parent, or `null` at the top level. */
    parent(id: RowLocalId): Option<RowLocalId>;
    /** Row `id`'s children, in order. */
    children(id: RowLocalId): readonly OrderedTree.Node<RowLocalId, Rational>[];
    /** The tree drawn as text, a node per line (`label` names a node; `null`: its id). */
    print(label: Option<(node: OrderedTree.Node<RowLocalId, Rational>) => string>): string;
  }

  /**
   * What `parseSheet` parses: the sheet's entry, every field stated
   * (`Network.UserSheet` — it becomes the handle's `network`, so a field
   * left out would read `undefined` there), and its rows. A
   * `Network.UserSheetWithRows` is one; a sheet the host builds states its
   * entry with `Network.UserSheet.make({ … })`.
   */
  export type ParseInput = Network.UserSheet & {
    rows: ParseRow[];
  };

  /**
   * Collect IDs of rows surfaced as the sheet footer.
   *
   * Footers are system rows tagged with `metadata.surface === "footer"`.
   * Replaced the legacy `isFooterRow: true` flag retired in §4 Phase D.4.
   */
  export function collectFooterRowIds(
    systemRows: readonly { id: RowId; metadata?: Option<Json>; children?: readonly RowId[] }[],
    ids: RowLocalId.Resolver,
  ): Set<RowLocalId> {
    const byId = new Map<string, typeof systemRows[number]>();
    for (const r of systemRows) byId.set(r.id.toString(), r);

    const out = new Set<RowLocalId>();
    const visit = (rid: RowId): void => {
      out.add(ids.resolve(RowKey.of(rid.toString())));
      const row = byId.get(rid.toString());
      if (row?.children) for (const c of row.children) visit(c);
    };

    for (const srow of systemRows) {
      if (Json.string(Json.object(srow.metadata ?? null), "surface") === "footer") {
        visit(srow.id);
      }
    }
    return out;
  }

  /**
   * Convert a rows-side `RawSheet` into the legacy `LonaSdk.EphemeralSheet`
   * shape consumed by `showEphemeralSheet` / `sdk.openEphemeral`.
   *
   * Inline `RawRow.cells` (catalog demo seeds) become `EphemeralRow.data`
   * entries so `openEphemeralHandle` seeds them into the in-memory
   * backend; a cell's `cellId` rides along as the entry's third element
   * (a `multi`-cardinality row's cells cannot be installed without it).
   * `ownerId` is supplied by the caller (typically
   * `client.ids.currentUser ?? "local"`).
   */
  export function fromRaw(
    raw: RawSheet,
    ownerId: UserId,
  ): EphemeralSheets.EphemeralSheet {
    const rows: EphemeralSheets.EphemeralRow[] = raw.rows.map((row) => ({
      id: RowId.of(row.id),
      type: row.type,
      lookupKey: row.lookupKey ?? null,
      timeScale: timescaleOf(row.timeScale ?? null),
      label: row.label ?? null,
      formula: row.formula ?? null,
      attributes: row.attributes ?? null,
      heightPx: row.heightPx ?? null,
      icon: null,
      position: null,
      clientId: null,
      // Preserve the authored parent→child structure. Without this the
      // ephemeral conversion flattens every catalog row to a top-level root,
      // so a catalog row with authored children (e.g. the `:calendar` header
      // `surface` whose child is the weather alias) loses its nesting and the
      // children land in the body instead of inside their parent.
      // (Alias-expanded children are re-derived at hydrate time, so this only
      // matters for statically-authored catalog nesting.)
      children: row.children != null && row.children.length > 0
        ? row.children.map((child) => RowId.of(child))
        : null,
      data: row.cells != null && row.cells.length > 0
        ? row.cells.map((cell): EphemeralSheets.SeedCell => {
          const value = Json.wire(cell.value) ?? null;
          return cell.cellId != null ? [cell.date, value, cell.cellId] : [cell.date, value];
        })
        : null,
    }));
    return {
      id: SheetNetworkKey.fromWire(raw.id),
      rows,
      title: raw.title ?? null,
      // A builtin definition's reserved key is part of the sheet, not of the
      // caller's context — carrying it here keeps `RawSheet` ↔ `EphemeralSheet`
      // lossless, so a promote from this shape still POSTs its `lookup_key`.
      lookupKey: raw.lookupKey ?? null,
      width: raw.width ?? null,
      creatorId: null,
      role: null,
      description: null,
      timeScale: null,
    };
  }
}

/**
 * A rows-side row's time scale as the wire's `TimeScaleCustom`: an absent
 * one is `"day"`, and so is a string the wire does not name.
 */
function timescaleOf(raw: Option<string>): Network.TimeScaleCustom {
  switch (raw) {
    case "year":
    case "month":
    case "week":
    case "day":
    case "custom":
      return raw;
    default:
      return "day";
  }
}
